import type {
  AssetCanonicalPayload,
  AssetDraftResult,
  CanonicalAssetConfirmations,
  CanonicalAssetItem,
  CanonicalAssetVariant,
  CanonicalPersistedAssetIds,
  CanonicalUnknownObject,
} from './types';
import { parseCanonicalPayloadBase } from './payload-base';

const GROUPS = ['characters', 'locations', 'props'] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function requireRecord(value: unknown, path: string): Record<string, unknown> {
  if (!isRecord(value)) throw new Error(`AI Session ${path} 必须是对象`);
  return value;
}

function requireString(value: unknown, path: string): string {
  if (typeof value !== 'string' || !value.trim()) {
    throw new Error(`AI Session ${path} 必须是非空字符串`);
  }
  return value;
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function stringList(value: unknown, path: string): readonly string[] {
  if (!Array.isArray(value) || value.some((item) => typeof item !== 'string')) {
    throw new Error(`AI Session ${path} 必须是字符串数组`);
  }
  return value;
}

function extensionsOf(
  record: Record<string, unknown>,
  declaredKeys: readonly string[]
): CanonicalUnknownObject | undefined {
  const declared = new Set(declaredKeys);
  const entries = Object.entries(record).filter(([key]) => !declared.has(key));
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

const VARIANT_KEYS = [
  'variantDraftId',
  'kind',
  'tagLabel',
  'description',
  'isDefault',
  'visualDescription',
  'clothing',
  'hair',
  'makeup',
  'accessories',
  'voiceName',
  'voiceId',
  'applicableScenes',
  'timeOfDay',
  'weather',
  'season',
  'atmosphere',
  'usage',
  'props',
  'metaData',
] as const;

function parseVariant(value: unknown, path: string): CanonicalAssetVariant {
  const record = requireRecord(value, path);
  const metaData =
    record.metaData === undefined ? {} : requireRecord(record.metaData, `${path}.metaData`);
  const extensions = extensionsOf(record, VARIANT_KEYS);
  return {
    variantDraftId: requireString(record.variantDraftId, `${path}.variantDraftId`),
    kind: requireString(record.kind, `${path}.kind`),
    tagLabel: requireString(record.tagLabel, `${path}.tagLabel`),
    ...Object.fromEntries(
      [
        'description',
        'visualDescription',
        'clothing',
        'hair',
        'makeup',
        'timeOfDay',
        'weather',
        'season',
        'atmosphere',
        'usage',
      ].flatMap((key) =>
        typeof record[key] === 'string' ||
        (['timeOfDay', 'weather', 'season'].includes(key) && record[key] === null)
          ? [[key, record[key]]]
          : []
      )
    ),
    isDefault: record.isDefault === true,
    accessories: stringList(record.accessories ?? [], `${path}.accessories`),
    ...(optionalString(record.voiceName) ? { voiceName: optionalString(record.voiceName) } : {}),
    ...(optionalString(record.voiceId) ? { voiceId: optionalString(record.voiceId) } : {}),
    applicableScenes: stringList(record.applicableScenes ?? [], `${path}.applicableScenes`),
    props: stringList(record.props ?? [], `${path}.props`),
    metaData,
    ...(extensions ? { extensions } : {}),
  };
}

const ITEM_KEYS = [
  'schemaVersion',
  'draftItemId',
  'kind',
  'name',
  'description',
  'roleType',
  'isOffscreen',
  'gender',
  'race',
  'visualAge',
  'location',
  'realWorldLocation',
  'propType',
  'ownerCharacter',
  'ownerLocation',
  'usage',
  'defaultVariantDraftId',
  'variants',
  'metaData',
] as const;

function parseItem(value: unknown, path: string): CanonicalAssetItem {
  const record = requireRecord(value, path);
  const variants = record.variants ?? [];
  if (!Array.isArray(variants)) throw new Error(`AI Session ${path}.variants 必须是数组`);
  const schemaVersion = record.schemaVersion ?? 2;
  if (typeof schemaVersion !== 'number' || !Number.isFinite(schemaVersion)) {
    throw new Error(`AI Session ${path}.schemaVersion 必须是有限数字`);
  }
  const metaData =
    record.metaData === undefined ? {} : requireRecord(record.metaData, `${path}.metaData`);
  const extensions = extensionsOf(record, ITEM_KEYS);
  const optionalStrings = Object.fromEntries(
    [
      'description',
      'roleType',
      'gender',
      'race',
      'visualAge',
      'location',
      'realWorldLocation',
      'propType',
      'ownerCharacter',
      'ownerLocation',
      'usage',
      'defaultVariantDraftId',
    ].flatMap((key) => {
      const value =
        (key !== 'defaultVariantDraftId' && typeof record[key] === 'string') ||
        (['location', 'propType'].includes(key) && record[key] === null)
          ? record[key]
          : optionalString(record[key]);
      return value === undefined ? [] : [[key, value]];
    })
  );
  return {
    schemaVersion,
    draftItemId: requireString(record.draftItemId, `${path}.draftItemId`),
    kind: requireString(record.kind, `${path}.kind`),
    name: requireString(record.name, `${path}.name`),
    ...optionalStrings,
    ...(typeof record.isOffscreen === 'boolean' ? { isOffscreen: record.isOffscreen } : {}),
    variants: variants.map((variant, index) => parseVariant(variant, `${path}.variants[${index}]`)),
    metaData,
    ...(extensions ? { extensions } : {}),
  };
}

export function parseAssetDraftResult(value: unknown, path: string): AssetDraftResult {
  const record = requireRecord(value, path);
  const parseGroup = (group: (typeof GROUPS)[number]) => {
    const items = record[group];
    if (!Array.isArray(items)) throw new Error(`AI Session ${path}.${group} 必须是数组`);
    return items.map((item, index) => parseItem(item, `${path}.${group}[${index}]`));
  };
  const schemaVersion = record.schemaVersion;
  if (
    schemaVersion !== undefined &&
    (typeof schemaVersion !== 'number' || !Number.isFinite(schemaVersion))
  ) {
    throw new Error(`AI Session ${path}.schemaVersion 必须是有限数字`);
  }
  return {
    ...(typeof schemaVersion === 'number' ? { schemaVersion } : {}),
    characters: parseGroup('characters'),
    locations: parseGroup('locations'),
    props: parseGroup('props'),
  };
}

function parseConfirmations(value: unknown): CanonicalAssetConfirmations {
  const record = requireRecord(value, 'assetConfirmations');
  return Object.fromEntries(
    GROUPS.flatMap((group) => {
      if (record[group] === undefined) return [];
      const state = requireRecord(record[group], `assetConfirmations.${group}`);
      if (typeof state.names !== 'boolean' || typeof state.labels !== 'boolean') {
        throw new Error(`AI Session assetConfirmations.${group} 必须包含布尔 names/labels`);
      }
      return [[group, { names: state.names, labels: state.labels }]];
    })
  );
}

function parsePersistedIds(value: unknown): CanonicalPersistedAssetIds {
  const record = requireRecord(value, 'persistedAssetIds');
  return Object.fromEntries(
    GROUPS.map((group) => [group, stringList(record[group], `persistedAssetIds.${group}`)])
  ) as CanonicalPersistedAssetIds;
}

export function parseAssetCanonicalPayload(
  payload: Record<string, unknown>
): AssetCanonicalPayload {
  return {
    ...parseCanonicalPayloadBase(payload),
    ...(payload.assetConfirmations !== undefined
      ? { assetConfirmations: parseConfirmations(payload.assetConfirmations) }
      : {}),
    ...(payload.assetResult !== undefined
      ? { assetResult: parseAssetDraftResult(payload.assetResult, 'assetResult') }
      : {}),
    ...(payload.assetChecklistDraft !== undefined
      ? {
          assetChecklistDraft: parseAssetDraftResult(
            payload.assetChecklistDraft,
            'assetChecklistDraft'
          ),
        }
      : {}),
    ...(payload.persistedAssetIds !== undefined
      ? { persistedAssetIds: parsePersistedIds(payload.persistedAssetIds) }
      : {}),
    ...(typeof payload.assetVersion === 'number' ? { assetVersion: payload.assetVersion } : {}),
  };
}
